import React, { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  IconButton,
  Avatar,
  Button,
  Menu,
  MenuItem,
  Badge,
  useMediaQuery,
  useTheme,
  Divider,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Settings as SettingsIcon,
  Help,
  Notifications,
  KeyboardArrowDown,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { apiRequest } from "../utils/apiRequest";
import { API } from "../api/endpoints";
import { DRAWER_WIDTH } from "../constants/layout";

const drawerWidth = DRAWER_WIDTH;

const Header = ({ onMenuToggle, onLogout }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [userData, setUserData] = useState(null);
  const [notificationCount, setNotificationCount] = useState(0);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const navigate = useNavigate();

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const getUserTitle = (role) => {
    switch (role?.toLowerCase()) {
      case "admin":
        return "Admin";
      case "executive":
        return "Executive";
      case "department_head":
        return "Department Head";
      case "standard":
        return "User";
      default:
        return "User"; // Default fallback
    }
  };

  const currentDateTime = new Date().toLocaleString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    day: "numeric",
    month: "long",
    year: "numeric",
    weekday: "long",
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const response = await apiRequest({
          ...API.userProfile,
        });

        setUserData(response?.data?.user);
        setNotificationCount(response?.data?.notification_count || 0);
      } catch (error) {
        console.error("Failed to fetch user profile:", error);
      }
    };

    fetchUserProfile();
  }, []);

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)`, xs: "100%" },
          ml: { sm: `${drawerWidth}px`, xs: 0 },
          zIndex: 1200,
          transition: "all 0.3s ease",
          color: "#333",
          background: isScrolled
            ? "linear-gradient(180deg, #ffffff 0%, #f9fafb 100%)"
            : "#ffffff",
          boxShadow: isScrolled
            ? "0 4px 12px rgba(0,0,0,0.1)"
            : "0 2px 4px rgba(0,0,0,0.05)",
          borderBottom: isScrolled ? "1px solid #e0e0e0" : "none",
        }}
        elevation={0}
      >
        <Toolbar
          sx={{
            justifyContent: "space-between",
            py: { xs: 0.5, sm: 1 },
            px: { xs: 1, sm: 2 },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {isMobile && (
              <IconButton
                edge="start"
                onClick={onMenuToggle}
                sx={{
                  color: "#424242",
                  "&:hover": { bgcolor: "#f0f0f0" },
                  mr: 0.5,
                }}
              >
                <MenuIcon sx={{ fontSize: 24 }} />
              </IconButton>
            )}
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  fontSize: {
                    xs: "0.85rem",
                    sm: "0.95rem",
                    md: "1.125rem",
                  },
                  lineHeight: 1.2,
                  color: "#333",
                }}
              >
                Welcome {userData?.name || ""}!
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "#666",
                  fontSize: {
                    xs: "0.7rem",
                    sm: "0.75rem",
                    md: "0.875rem",
                  },
                  fontWeight: 500,
                  display: "block",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: isMobile ? "120px" : "none",
                }}
              >
                {currentDateTime}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {!isMobile && (
              <>
                {/* <IconButton
                  onClick={() => navigate("/dashboard/settings")}
                  sx={{
                    color: "#424242",
                    "&:hover": { bgcolor: "#f0f0f0" },
                    p: { xs: 0.5, sm: 0.75 },
                  }}
                >
                  <SettingsIcon sx={{ fontSize: 20 }} />
                </IconButton>
                <IconButton
                  sx={{
                    color: "#424242",
                    "&:hover": { bgcolor: "#f0f0f0" },
                    p: { xs: 0.5, sm: 0.75 },
                  }}
                >
                  <Help sx={{ fontSize: 20 }} />
                </IconButton> */}
                <IconButton
                  onClick={() => navigate("/dashboard/notifications")}
                  sx={{
                    color: "#424242",
                    "&:hover": { bgcolor: "#f0f0f0" },
                    p: { xs: 0.5, sm: 0.75 },
                  }}
                >
                  <Badge
                    badgeContent={notificationCount}
                    color="error"
                    sx={{
                      "& .MuiBadge-badge": {
                        fontSize: "0.65rem",
                        height: 18,
                        minWidth: 18,
                        bgcolor: "#DA291C",
                      },
                    }}
                  >
                    <Notifications sx={{ fontSize: 20 }} />
                  </Badge>
                </IconButton>
              </>
            )}
            <Divider
              orientation="vertical"
              variant="middle"
              flexItem
              sx={{
                // borderWidth: 1,
                borderColor: "#666666",
                height: "30px",
                marginLeft: 1,
                marginRight: 1,
              }}
            />
            <Button
              onClick={handleProfileMenuOpen}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                textTransform: "none",
                color: "#333",
                p: { xs: 0.5, sm: 0.75 },
                borderRadius: 8,
                "&:hover": { bgcolor: "#f0f0f0" },
                minWidth: "auto",
              }}
            >
              <Avatar
                src={userData?.profile_img || ""}
                sx={{
                  width: { xs: 32, sm: 36 },
                  height: { xs: 32, sm: 36 },
                  // bgcolor: userData?.profile_img ? "transparent" : "#4CAF50",
                  // fontSize: { xs: "0.75rem", sm: "0.875rem" },
                  // fontWeight: 600,
                }}
              >
                {!userData?.profile_img && userData?.name?.charAt(0)}
              </Avatar>
              {!isMobile && (
                <Box sx={{ textAlign: "left", ml: 1, minWidth: 0 }}>
                  <Typography
                    noWrap
                    sx={{
                      fontWeight: 700,
                      fontSize: {
                        xs: "0.75rem",
                        sm: "0.875rem",
                      },
                      color: "#333",
                    }}
                  >
                    {userData?.name || "John Doe"}
                  </Typography>
                  <Typography
                    noWrap
                    variant="caption"
                    sx={{
                      color: "#4CAF50",
                      fontSize: {
                        xs: "0.65rem",
                        sm: "0.75rem",
                      },
                      fontWeight: 500,
                    }}
                  >
                    {getUserTitle(userData?.role)}
                  </Typography>
                </Box>
              )}
              {!isMobile && (
                <KeyboardArrowDown sx={{ fontSize: 20, color: "#666" }} />
              )}
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 160,
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            border: "1px solid #e0e0e0",
            borderRadius: 8,
            bgcolor: "#ffffff",
          },
        }}
      >
        <MenuItem
          onClick={() => {
            navigate("/dashboard/profile");
            handleMenuClose();
          }}
          sx={{ fontSize: "0.875rem", py: 1 }}
        >
          Profile
        </MenuItem>
        <MenuItem
          onClick={() => {
            navigate("/dashboard/change-password");
            handleMenuClose();
          }}
          sx={{ fontSize: "0.875rem", py: 1 }}
        >
          Change Password
        </MenuItem>
        {/* <MenuItem
          onClick={() => {
            navigate("/dashboard/settings");
            handleMenuClose();
          }}
          sx={{ fontSize: "0.875rem", py: 1 }}
        >
          Settings
        </MenuItem> */}
        <MenuItem
          onClick={() => {
            onLogout();
            handleMenuClose();
          }}
          sx={{
            fontSize: "0.875rem",
            py: 1,
            color: "#DA291C",
          }}
        >
          Logout
        </MenuItem>
      </Menu>
    </>
  );
};

export default Header;
